import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "components/ui/Select";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import { useGenerateOrderHistoryActualReport } from "http-hooks/agorder/useAgorder";
import { getDocumentUrls, getDocumentViewerRoute } from "utils/documentUrlHelper";

// Report type options
const reportTypeOptions = [
  { value: "V", label: "Vendorwise" },
  { value: "C", label: "Customerwise" }
];

const OrderHistory = () => {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState(null);
  const [selectedVendors, setSelectedVendors] = useState(null); // Can be single object or array
  const [selectedCustomers, setSelectedCustomers] = useState(null); // Can be single object or array

  // Mutation for generating report
  const { mutateAsync: generateReport, isPending: isGenerating } = useGenerateOrderHistoryActualReport();

  const handleReportTypeChange = (selected) => {
    setReportType(selected);
    // Reset selections when report type changes
    setSelectedVendors(null);
    setSelectedCustomers(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Extract IDs - handle both single object and array
    const vendorIds = Array.isArray(selectedVendors) 
      ? selectedVendors.map(v => v.ps_Id) 
      : selectedVendors?.ps_Id;
    
    const customerIds = Array.isArray(selectedCustomers) 
      ? selectedCustomers.map(c => c.ps_Id) 
      : selectedCustomers?.ps_Id;

    const formData = {
      reportType: reportType?.value,
      selectedVendors: vendorIds,
      selectedCustomers: customerIds
    };

    try {
      // Generate the report
      const data = await generateReport(formData);
      
      // Navigate to Document Viewer
      if (data?.htmlUrl) {
        const { htmlUrl, pdfUrl } = getDocumentUrls(data);
        
        const route = getDocumentViewerRoute('order-history', {
          htmlUrl,
          pdfUrl,
          documentNumber: 'Order History',
          documentType: reportType?.label + ' Report'
        });
        
        navigate(route);
      }
    } catch (error) {
      console.error("Error generating report:", error);
    }
  };

  const isFormValid = () => {
    if (!reportType) return false;
    
    if (reportType.value === "V") {
      // Vendorwise: need 1 vendor (single) and multiple customers (array)
      return selectedVendors !== null && 
             !Array.isArray(selectedVendors) && 
             Array.isArray(selectedCustomers) && 
             selectedCustomers.length > 0;
    } else if (reportType.value === "C") {
      // Customerwise: need 1 customer (single) and multiple vendors (array)
      return selectedCustomers !== null && 
             !Array.isArray(selectedCustomers) && 
             Array.isArray(selectedVendors) && 
             selectedVendors.length > 0;
    }
    
    return false;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold leading-6 text-gray-900">
            Orders History Generator
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Generate order history reports by vendor or customer
          </p>
        </div>
      </div>

      <div className="mt-8 bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl">
        <form onSubmit={handleSubmit}>
          <div className="px-4 py-6 sm:p-8">
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
              {/* Report Type */}
              <div className="sm:col-span-3">
                <Select
                  value={reportType}
                  onChange={handleReportTypeChange}
                  options={reportTypeOptions}
                  label="Report Type"
                  placeholder="Choose Here"
                  isRequired={true}
                  isClearable={false}
                />
              </div>

              {/* Vendor Selection */}
              {reportType && (
                <div className="sm:col-span-3">
                  {reportType.value === "V" ? (
                    // Vendorwise: Single vendor selection
                    <SelectVendors
                      value={selectedVendors}
                      onChange={setSelectedVendors}
                      label="Vendor"
                      placeholder="Choose Here"
                      isRequired={true}
                    />
                  ) : (
                    // Customerwise: Multiple vendors selection
                    <SelectVendors
                      value={selectedVendors}
                      onChange={setSelectedVendors}
                      label="Vendors"
                      placeholder="Choose Vendors"
                      isMulti={true}
                      isRequired={true}
                    />
                  )}
                </div>
              )}

              {/* Customer Selection */}
              {reportType && (
                <div className="sm:col-span-3">
                  {reportType.value === "C" ? (
                    // Customerwise: Single customer selection
                    <SelectCustomers
                      value={selectedCustomers}
                      onChange={setSelectedCustomers}
                      label="Customer"
                      placeholder="Choose Here"
                      isRequired={true}
                    />
                  ) : (
                    // Vendorwise: Multiple customers selection
                    <SelectCustomers
                      value={selectedCustomers}
                      onChange={setSelectedCustomers}
                      label="Customers"
                      placeholder="Choose Customers"
                      isMulti={true}
                      isRequired={true}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-center gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8">
            <button
              type="submit"
              disabled={!isFormValid() || isGenerating}
              className={`rounded-md px-8 py-2.5 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                isFormValid() && !isGenerating
                  ? "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              {isGenerating ? "Generating..." : "Generate Report"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderHistory;

